import {
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    ChangeDetectorRef,
    AfterViewInit,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import * as Feather from 'feather-icons';
import { WarehouseService } from 'app/services/warehouse/warehouse.service';
import { ConvertDocumentTarget } from '../convert-tick-caiss/convert-tick-caiss.component';

export type PosPaymentStatus = 'paye' | 'non_paye' | 'partiel';

export type PosTicketAvailability =
    | 'disponible'
    | 'consolide'
    | 'facture_client'
    | 'avoire'
    | 'annule';

export interface PosTicketRow {
    id: string;
    numero: string;
    date: string;
    clientName: string;
    clientPhone: string;
    entrepot: string;
    entrepotId: string | null;
    paymentStatus: PosPaymentStatus;
    paymentType: string;
    ticketStatus: PosTicketAvailability;
}

/** Ligne article affichée dans le panneau détail (données mock / API). */
export interface PosTicketLine {
    reference: string;
    barcode: string;
    colorSize: string;
    unitPrice: number;
}

/** Vue détaillée du ticket pour le panneau latéral. */
export interface PosTicketDetail {
    ticket: PosTicketRow;
    lines: PosTicketLine[];
    totalPrice: number;
    amountPaid: number;
}

interface WarehouseOption {
    id: string;
    label: string;
}

@Component({
    selector: 'app-poscontent',
    templateUrl: './poscontent.component.html',
    styleUrls: ['./poscontent.component.scss']
})
export class POSContentComponent implements OnInit, AfterViewInit, OnChanges {
    @ViewChild('selectAllCheckbox') selectAllCheckbox?: ElementRef<HTMLInputElement>;
    @ViewChild('datePicker') datePicker!: ElementRef;
    @ViewChild('dateJusquaAchat') dateJusquaAchat!: ElementRef;
    @ViewChild('dateDuVente') dateDuVente!: ElementRef;
    @ViewChild('dateJusquaVente') dateJusquaVente!: ElementRef;
    /** Statut d’onglet parent (ex. Tous, Disponible) — utilisé pour filtrer la liste. */
    @Input() status = 'Tous';
    isLoadingExpanded = false;
    viewMode: 'jours' | 'mois' | 'annees' = 'jours';
    weekDays: { label: string; dateString: string; isToday: boolean }[] = [];
    currentMonthIndex = new Date().getMonth();
    currentYear = new Date().getFullYear();
    selectedMonthIndex: number | null = null;
    selectedDay: string = '';
    selectedYear: number | null = null;
    selectedDate: Date | null = null;
    selectedPeriod = '';
    dateFilterStart: string = '';
    dateFilterEnd: string = '';
    months = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    years: number[] = [];
    filters: any = {
        raison: '',
        matricule: '',
        chiffre_aff: '',
        operator: '=',
        client_id: null,
        fournisseur_id: null,
        projet_id: null,
        date_du: '',
        date_jusqua: '',
        selectedStatuses: []
    };

    /** Aligné sur shared.component */
    searchTerm = '';
    selectedWarehouseIds: string[] = [];
    selectedPaymentTypes: string[] = [];
    showFilter = false;

    filteredData: PosTicketRow[] = [];
    paginatedData: PosTicketRow[] = [];
    basicSelectedOption = 10;
    totalPages = 1;
    pages: number[] = [];

    readonly ticketTableColspan = 9;

    warehouseOptions: WarehouseOption[] = [];

    paymentTypeOptions = ['Espèce', 'Carte bancaire', 'Sodexo / Espèce', 'Chèque', 'Virement'];

    /** Données d’exemple — à remplacer par un appel API. */
    allTickets: PosTicketRow[] = [];

    selectedIds = new Set<string>();

    currentPage = 1;

    /** Panneau latéral « détails ticket de caisse » */
    ticketDetail: PosTicketDetail | null = null;

    /** Modale conversion ticket → document (app-convert-tick-caiss) */
    showConvertModal = false;
    ticketToConvert: PosTicketRow | null = null;

    /** Confirmation suppression ticket */
    showDeleteConfirmModal = false;
    ticketPendingDelete: PosTicketRow | null = null;

    constructor(
        private cdr: ChangeDetectorRef,
        private warehouseService: WarehouseService
    ) {}

    ngOnInit(): void {
        const today = new Date();
        this.selectedDate = today;
        this.selectedDay = today.toISOString().split('T')[0];
        this.selectedMonthIndex = today.getMonth();
        this.selectedYear = today.getFullYear();
        this.dateFilterStart = this.selectedDay;
        this.dateFilterEnd = this.selectedDay;
        this.updateSelectedPeriod();
        this.generateWeekDays();
        this.years = Array.from({ length: 10 }, (_, i) => this.currentYear - i).reverse();
        this.loadWarehouseOptions();
    }

    ngAfterViewInit(): void {
        this.refreshFeatherIcons();
        this.syncMasterCheckbox();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['status'] && !changes['status'].firstChange) {
            this.currentPage = 1;
            this.recomputeList();
        }
    }

    get allPageSelected(): boolean {
        if (this.paginatedData.length === 0) {
            return false;
        }
        return this.paginatedData.every((t) => this.selectedIds.has(t.id));
    }

    trackByTicketId(_: number, row: PosTicketRow): string {
        return row.id;
    }

    trackByFilterKey(_: number, f: { key: string }): string {
        return f.key;
    }

    getActiveFilters(): { key: string; label: string; value: string }[] {
        const chips: { key: string; label: string; value: string }[] = [];
        if (this.selectedWarehouseIds.length) {
            const labels = this.selectedWarehouseIds.map((id) => {
                const w = this.warehouseOptions.find((x) => x.id === id);
                return w?.label ?? id;
            });
            chips.push({ key: 'warehouse', label: 'Entrepôt', value: labels.join(', ') });
        }
        if (this.selectedPaymentTypes.length) {
            chips.push({ key: 'paymentType', label: 'Paiement', value: this.selectedPaymentTypes.join(', ') });
        }
        return chips;
    }

    toggleFilter(): void {
        this.showFilter = !this.showFilter;
        this.refreshFeatherIcons();
    }

    onSearch(): void {
        this.currentPage = 1;
        this.recomputeList();
        this.refreshFeatherIcons();
    }

    onWarehouseFilterChange(): void {
        this.currentPage = 1;
        this.recomputeList();
        this.refreshFeatherIcons();
    }

    onPaymentTypeFilterChange(): void {
        this.currentPage = 1;
        this.recomputeList();
        this.refreshFeatherIcons();
    }

    removeFilter(key: string, _event?: Event): void {
        if (key === 'warehouse') {
            this.selectedWarehouseIds = [];
        }
        if (key === 'paymentType') {
            this.selectedPaymentTypes = [];
        }
        this.currentPage = 1;
        this.recomputeList();
        this.refreshFeatherIcons();
    }

    updatePagination(): void {
        this.totalPages = Math.ceil(this.filteredData.length / this.basicSelectedOption) || 1;
        this.currentPage = Math.min(this.currentPage, this.totalPages);
        this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        const start = (this.currentPage - 1) * this.basicSelectedOption;
        this.paginatedData = this.filteredData.slice(start, start + this.basicSelectedOption);
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
        this.syncMasterCheckbox();
    }

    changePage(page: number): void {
        this.currentPage = page;
        this.updatePagination();
    }

    toggleSelectAllOnPage(checked: boolean): void {
        for (const t of this.paginatedData) {
            if (checked) {
                this.selectedIds.add(t.id);
            } else {
                this.selectedIds.delete(t.id);
            }
        }
        this.cdr.detectChanges();
        this.syncMasterCheckbox();
    }

    toggleTicketSelection(id: string, checked: boolean): void {
        if (checked) {
            this.selectedIds.add(id);
        } else {
            this.selectedIds.delete(id);
        }
        this.cdr.detectChanges();
        this.syncMasterCheckbox();
    }

    /** État indeterminé de la case « tout » quand seule une partie de la page est cochée. */
    private syncMasterCheckbox(): void {
        queueMicrotask(() => {
            const el = this.selectAllCheckbox?.nativeElement;
            if (!el) {
                return;
            }
            const rows = this.paginatedData;
            if (rows.length === 0) {
                el.indeterminate = false;
                return;
            }
            const selectedCount = rows.filter((r) => this.selectedIds.has(r.id)).length;
            el.indeterminate = selectedCount > 0 && selectedCount < rows.length;
        });
    }

    isTicketSelected(id: string): boolean {
        return this.selectedIds.has(id);
    }

    /** Clic sur la ligne : bascule la case (ignore checkbox, liens, boutons, colonne actions). */
    onTicketRowClick(event: MouseEvent, item: PosTicketRow): void {
        const el = event.target as HTMLElement | null;
        if (!el) {
            return;
        }
        if (el.closest('.pos-ticket-table__td-check') || el.closest('input[type="checkbox"]')) {
            return;
        }
        if (el.closest('a') || el.closest('button')) {
            return;
        }
        if (el.closest('.action-icons')) {
            return;
        }
        this.toggleTicketSelection(item.id, !this.isTicketSelected(item.id));
    }

    formatTicketDate(iso: string): string {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) {
            return '';
        }
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd} / ${mm} / ${yyyy}`;
    }

    paymentStatusLabel(s: PosPaymentStatus): string {
        const m: Record<PosPaymentStatus, string> = {
            paye: 'Payé',
            non_paye: 'Non payé',
            partiel: 'Partiellement payé'
        };
        return m[s];
    }

    getPaymentBadgeNgClass(item: PosTicketRow): Record<string, boolean> {
        const s = item.paymentStatus;
        return {
            badge: true,
            'badge-paye': s === 'paye',
            'badge-nonPaye': s === 'non_paye',
            'badge-partiellement-payé': s === 'partiel'
        };
    }

    getTicketBadgeNgClass(item: PosTicketRow): Record<string, boolean> {
        const st = item.ticketStatus;
        return {
            badge: true,
            'badge-accepte': st === 'disponible',
            'badge-ferme': st === 'consolide',
            'badge-cree': st === 'facture_client',
            'badge-negociation': st === 'avoire',
            'badge-annule': st === 'annule'
        };
    }

    ticketStatusLabel(s: PosTicketAvailability): string {
        const m: Record<PosTicketAvailability, string> = {
            disponible: 'Disponible',
            consolide: 'Consolidé',
            facture_client: 'Facture client',
            avoire: 'Avoiré',
            annule: 'Annulé'
        };
        return m[s];
    }

    onViewTicket(t: PosTicketRow): void {
        this.openTicketDetailPanel(t);
    }

    openTicketDetailPanel(row: PosTicketRow): void {
        this.ticketDetail = this.buildTicketDetail(row);
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    closeTicketDetailPanel(): void {
        this.ticketDetail = null;
        this.cdr.detectChanges();
    }

    onClose(): void {
        this.closeTicketDetailPanel();
    }

    onContentClick(event: Event): void {
        event.stopPropagation();
    }

    formatMoneyTnd(value: number): string {
        return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT`;
    }

    trackByLineRef(_: number, line: PosTicketLine): string {
        return line.reference + line.barcode;
    }

    /** Affichage COULEUR / TAILLE sur deux lignes (maquette). */
    colorSizeMain(line: PosTicketLine): string {
        const s = (line.colorSize || '').trim();
        const i = s.lastIndexOf(' ');
        return i > 0 ? s.slice(0, i) : s;
    }

    colorSizeSub(line: PosTicketLine): string {
        const s = (line.colorSize || '').trim();
        const i = s.lastIndexOf(' ');
        return i > 0 ? s.slice(i + 1) : '';
    }

    formatUnitPrice(value: number): string {
        return value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    }

    /** Données détail — à remplacer par un appel API getTicketById. */
    private buildTicketDetail(row: PosTicketRow): PosTicketDetail {
        const seed = row.id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
        const lines: PosTicketLine[] = [
            {
                reference: `ere558r7e57${(seed % 9).toString()}`,
                barcode: '2225555658745',
                colorSize: 'Bleu XL',
                unitPrice: 125
            },
            {
                reference: `SKU-${row.numero.replace(/\W/g, '').slice(-6) || '000001'}`,
                barcode: `${3800000000000 + (seed % 999999)}`,
                colorSize: 'Noir L',
                unitPrice: 89.5 + (seed % 10)
            }
        ];
        const sumLines = lines.reduce((s, l) => s + l.unitPrice, 0);
        const totalPrice = Math.round((sumLines * (1.15 + (seed % 20) / 100)) * 1000) / 1000;
        let amountPaid = totalPrice;
        if (row.paymentStatus === 'non_paye') {
            amountPaid = 0;
        } else if (row.paymentStatus === 'partiel') {
            amountPaid = Math.round(totalPrice * 0.5 * 1000) / 1000;
        }
        return { ticket: row, lines, totalPrice, amountPaid };
    }

    onSyncTicket(t: PosTicketRow): void {
        void t;
    }

    openDeleteConfirmModal(row: PosTicketRow): void {
        this.ticketPendingDelete = row;
        this.showDeleteConfirmModal = true;
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    closeDeleteConfirmModal(): void {
        this.showDeleteConfirmModal = false;
        this.ticketPendingDelete = null;
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    confirmDeleteTicket(): void {
        const row = this.ticketPendingDelete;
        if (!row) {
            return;
        }
        this.allTickets = this.allTickets.filter((t) => t.id !== row.id);
        this.selectedIds.delete(row.id);
        if (this.ticketDetail?.ticket.id === row.id) {
            this.closeTicketDetailPanel();
        }
        this.closeDeleteConfirmModal();
        this.recomputeList();
    }

    openConvertModal(row: PosTicketRow): void {
        this.ticketToConvert = row;
        this.showConvertModal = true;
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    closeConvertModal(): void {
        this.showConvertModal = false;
        this.ticketToConvert = null;
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    onConvertDocument(target: ConvertDocumentTarget): void {
        const ticket = this.ticketToConvert;
        void target;
        void ticket;
        this.closeConvertModal();
    }

    selectDay(day: any) {
        this.selectedDay = day.dateString;
        this.selectedDate = new Date(day.dateString);
        this.selectedMonthIndex = this.selectedDate.getMonth();
        this.selectedYear = this.selectedDate.getFullYear();
        this.dateFilterStart = day.dateString;
        this.dateFilterEnd = day.dateString;
        this.updateSelectedPeriod();
        if (this.datePicker?.nativeElement?._flatpickr) {
            this.datePicker.nativeElement._flatpickr.setDate([day.dateString, day.dateString], false);
        }
        this.applyDateFilter(day.dateString, day.dateString);
    }

    selectMonth(index: number) {
        this.selectedMonthIndex = index;
        const year = this.selectedYear !== null ? this.selectedYear : this.currentYear;
        const monthStart = new Date(year, index, 1).toISOString().split('T')[0];
        const monthEnd = new Date(year, index + 1, 0).toISOString().split('T')[0];
        this.dateFilterStart = monthStart;
        this.dateFilterEnd = monthEnd;
        this.selectedPeriod = `Mois: ${this.months[index]} ${year}`;
        this.selectedDate = new Date(year, index, 1);
        this.selectedYear = year;
        this.selectedDay = '';
        if (this.datePicker?.nativeElement?._flatpickr) {
            this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
        }
        this.applyDateFilter(monthStart, monthEnd);
    }

    selectYear(year: number) {
        this.selectedYear = year;
        if (this.viewMode === 'annees') {
            const yearStart = `${year}-01-01`;
            const yearEnd = `${year}-12-31`;
            this.dateFilterStart = yearStart;
            this.dateFilterEnd = yearEnd;
            this.selectedPeriod = `Année: ${year}`;
            this.selectedDate = new Date(year, 0, 1);
            this.selectedMonthIndex = null;
            this.selectedDay = '';
            if (this.datePicker?.nativeElement?._flatpickr) {
                this.datePicker.nativeElement._flatpickr.setDate([yearStart, yearEnd], false);
            }
            this.applyDateFilter(yearStart, yearEnd);
        } else if (this.viewMode === 'mois') {
            const monthIndex = this.selectedMonthIndex !== null ? this.selectedMonthIndex : this.currentMonthIndex;
            const monthStart = new Date(year, monthIndex, 1).toISOString().split('T')[0];
            const monthEnd = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];
            this.dateFilterStart = monthStart;
            this.dateFilterEnd = monthEnd;
            this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
            this.selectedDate = new Date(year, monthIndex, 1);
            this.selectedMonthIndex = monthIndex;
            if (this.datePicker?.nativeElement?._flatpickr) {
                this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
            }
            this.applyDateFilter(monthStart, monthEnd);
        } else {
            const yearStart = `${year}-01-01`;
            const yearEnd = `${year}-12-31`;
            this.dateFilterStart = yearStart;
            this.dateFilterEnd = yearEnd;
            this.selectedPeriod = `Année: ${year}`;
            this.applyDateFilter(yearStart, yearEnd);
        }
    }

    incrementDate() {
        if (this.selectedDate) {
            if (this.viewMode === 'mois') {
                this.selectedDate.setMonth(this.selectedDate.getMonth() + 1);
                const year = this.selectedDate.getFullYear();
                const monthIndex = this.selectedDate.getMonth();
                const monthStart = new Date(year, monthIndex, 1).toISOString().split('T')[0];
                const monthEnd = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];
                this.dateFilterStart = monthStart;
                this.dateFilterEnd = monthEnd;
                this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
                this.selectedMonthIndex = monthIndex;
                this.selectedYear = year;
                this.selectedDay = '';
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
                }

                this.applyDateFilter(monthStart, monthEnd);
            } else if (this.viewMode === 'annees') {
                this.selectedDate.setFullYear(this.selectedDate.getFullYear() + 1);
                const year = this.selectedDate.getFullYear();
                const yearStart = `${year}-01-01`;
                const yearEnd = `${year}-12-31`;
                this.dateFilterStart = yearStart;
                this.dateFilterEnd = yearEnd;
                this.selectedPeriod = `Année: ${year}`;
                this.selectedYear = year;
                this.selectedMonthIndex = null;
                this.selectedDay = '';
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([yearStart, yearEnd], false);
                }

                this.applyDateFilter(yearStart, yearEnd);
            } else {
                this.selectedDate.setDate(this.selectedDate.getDate() + 1);
                const dayToUse = this.selectedDate.toISOString().split('T')[0];
                this.selectedDay = dayToUse;
                this.dateFilterStart = dayToUse;
                this.dateFilterEnd = dayToUse;
                this.selectedMonthIndex = this.selectedDate.getMonth();
                this.selectedYear = this.selectedDate.getFullYear();
                this.updateSelectedPeriod();
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([dayToUse, dayToUse], false);
                }

                this.applyDateFilter(dayToUse, dayToUse);
            }
            this.generateWeekDays();
            this.cdr.detectChanges();
        }
    }

    decrementDate() {
        if (this.selectedDate) {
            if (this.viewMode === 'mois') {
                this.selectedDate.setMonth(this.selectedDate.getMonth() - 1);
                const year = this.selectedDate.getFullYear();
                const monthIndex = this.selectedDate.getMonth();
                const monthStart = new Date(year, monthIndex, 1).toISOString().split('T')[0];
                const monthEnd = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];
                this.dateFilterStart = monthStart;
                this.dateFilterEnd = monthEnd;
                this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
                this.selectedMonthIndex = monthIndex;
                this.selectedYear = year;
                this.selectedDay = '';
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
                }

                this.applyDateFilter(monthStart, monthEnd);
            } else if (this.viewMode === 'annees') {
                this.selectedDate.setFullYear(this.selectedDate.getFullYear() - 1);
                const year = this.selectedDate.getFullYear();
                const yearStart = `${year}-01-01`;
                const yearEnd = `${year}-12-31`;
                this.dateFilterStart = yearStart;
                this.dateFilterEnd = yearEnd;
                this.selectedPeriod = `Année: ${year}`;
                this.selectedYear = year;
                this.selectedMonthIndex = null;
                this.selectedDay = '';
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([yearStart, yearEnd], false);
                }

                this.applyDateFilter(yearStart, yearEnd);
            } else {
                this.selectedDate.setDate(this.selectedDate.getDate() - 1);
                const dayToUse = this.selectedDate.toISOString().split('T')[0];
                this.selectedDay = dayToUse;
                this.dateFilterStart = dayToUse;
                this.dateFilterEnd = dayToUse;
                this.selectedMonthIndex = this.selectedDate.getMonth();
                this.selectedYear = this.selectedDate.getFullYear();
                this.updateSelectedPeriod();
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([dayToUse, dayToUse], false);
                }

                this.applyDateFilter(dayToUse, dayToUse);
            }
            this.generateWeekDays();
            this.cdr.detectChanges();
        }
    }

    private applyDateFilter(startDate: string, endDate: string): void {
        this.dateFilterStart = startDate;
        this.dateFilterEnd = endDate;
        this.currentPage = 1;
        this.recomputeList();
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    private formatPeriodJour(d: Date): string {
        return `Jour: ${d.getDate()} ${this.months[d.getMonth()]} ${d.getFullYear()}`;
    }

    updateSelectedPeriod() {
        const dateToUse = this.selectedDate || new Date();
        const year = dateToUse.getFullYear();
        if (this.viewMode === 'mois' && this.selectedMonthIndex !== null) {
            const selectedYear = this.selectedYear || year;
            this.selectedPeriod = `Mois: ${this.months[this.selectedMonthIndex]} ${selectedYear}`;
        } else if (this.viewMode === 'annees' && this.selectedYear !== null) {
            this.selectedPeriod = `Année: ${this.selectedYear}`;
        } else {
            this.selectedPeriod = this.formatPeriodJour(dateToUse);
        }
    }

    generateWeekDays() {
        const today = new Date();
        const startOfWeek = new Date(this.selectedDate || today);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);

        this.weekDays = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            return {
                label: d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' }),
                dateString: d.toISOString().split('T')[0],
                isToday: d.toDateString() === today.toDateString()
            };
        });
    }

    changeViewMode(mode: 'jours' | 'mois' | 'annees'): void {
        this.viewMode = mode;
        this.centerTodayItem();
        if (mode === 'jours') {
            const dayToUse = this.selectedDay || new Date().toISOString().split('T')[0];
            if (this.dateFilterStart !== dayToUse || this.dateFilterEnd !== dayToUse) {
                this.dateFilterStart = dayToUse;
                this.dateFilterEnd = dayToUse;
                this.selectedDay = dayToUse;
                this.selectedDate = new Date(dayToUse);
                this.applyDateFilter(dayToUse, dayToUse);
            } else {
                this.selectedDate = new Date(dayToUse);
            }
            this.updateSelectedPeriod();
        } else if (mode === 'mois') {
            const monthIndex = this.selectedMonthIndex !== null ? this.selectedMonthIndex : this.currentMonthIndex;
            const year = this.selectedYear !== null ? this.selectedYear : this.currentYear;
            this.selectedMonthIndex = monthIndex;
            this.selectedYear = year;
            const monthStart = new Date(year, monthIndex, 1).toISOString().split('T')[0];
            const monthEnd = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];
            this.dateFilterStart = monthStart;
            this.dateFilterEnd = monthEnd;
            this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
            this.selectedDate = new Date(year, monthIndex, 1);
            this.selectedDay = '';
            if (this.datePicker?.nativeElement?._flatpickr) {
                this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
            }

            this.applyDateFilter(monthStart, monthEnd);
        } else if (mode === 'annees') {
            const year = this.selectedYear !== null ? this.selectedYear : this.currentYear;
            this.selectedYear = year;
            this.selectedMonthIndex = null;
            this.selectedDay = '';
            const yearStart = `${year}-01-01`;
            const yearEnd = `${year}-12-31`;
            this.dateFilterStart = yearStart;
            this.dateFilterEnd = yearEnd;
            this.selectedPeriod = `Année: ${year}`;
            this.selectedDate = new Date(year, 0, 1);
            if (this.datePicker?.nativeElement?._flatpickr) {
                this.datePicker.nativeElement._flatpickr.setDate([yearStart, yearEnd], false);
            }
            this.applyDateFilter(yearStart, yearEnd);
        }
    }

    centerTodayItem() {
        setTimeout(() => {
            if (this.viewMode === 'jours') {
                const todayElement = document.querySelector('.day-item.today');
                if (todayElement) {
                    todayElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
            } else if (this.viewMode === 'mois') {
                const todayElement = document.querySelector('.month-item.today');
                if (todayElement) {
                    todayElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
            } else if (this.viewMode === 'annees') {
                const todayElement = document.querySelector('.year-item.today');
                if (todayElement) {
                    todayElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
            }
        }, 100);
    }

    openFlatpickr() {
        if (this.datePicker?.nativeElement?._flatpickr) {
            this.datePicker.nativeElement._flatpickr.open();
        }
    }

    private refreshFeatherIcons(): void {
        setTimeout(() => {
            try {
                Feather.replace();
            } catch {
                /* environnement test / SSR */
            }
            this.cdr.markForCheck();
        }, 0);
    }

    private normalizeTabKey(): string {
        const raw = (this.status || 'Tous')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '_');
        const aliases: Record<string, string> = {
            factureclient: 'facture_client',
            facture_clt: 'facture_client'
        };
        return aliases[raw] ?? raw;
    }

    private rowMatchesTab(row: PosTicketRow): boolean {
        const tab = this.normalizeTabKey();
        if (tab === 'tous') {
            return true;
        }
        return row.ticketStatus === tab;
    }

    private dateInFilterRange(isoDate: string): boolean {
        const d = isoDate.split('T')[0];
        return d >= this.dateFilterStart && d <= this.dateFilterEnd;
    }

    private recomputeList(): void {
        const kw = this.searchTerm.trim().toLowerCase();
        this.filteredData = this.allTickets.filter((row) => {
            if (!this.rowMatchesTab(row)) {
                return false;
            }
            if (!this.dateInFilterRange(row.date)) {
                return false;
            }
            if (this.selectedWarehouseIds.length && !this.selectedWarehouseIds.includes(String(row.entrepotId ?? ''))) {
                return false;
            }
            if (this.selectedPaymentTypes.length && !this.selectedPaymentTypes.includes(String(row.paymentType ?? ''))) {
                return false;
            }
            if (kw) {
                const blob = `${row.numero} ${row.clientName} ${row.clientPhone} ${row.entrepot} ${row.paymentType}`.toLowerCase();
                if (!blob.includes(kw)) {
                    return false;
                }
            }
            return true;
        });
        this.updatePagination();
    }

    /**
     * GET /api/entrepots?user_id=&station_id= (WarehouseService — base environment.stock_url).
     */
    private loadWarehouseOptions(): void {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const userId = Number(currentUser?.id);
        const stationId = Number(
            currentUser?.stations?.[0]?.station_id ??
                currentUser?.stations?.[0]?.id ??
                currentUser?.station_id ??
                0
        );
        if (!userId || !stationId) {
            this.warehouseOptions = [];
            this.seedMockTickets();
            this.recomputeList();
            return;
        }
        this.warehouseService.getAllWarehouses(userId, stationId).subscribe({
            next: (list) => {
                this.warehouseOptions = (list || []).map((w: Record<string, unknown>) => {
                    const rawId = w['id'] ?? w['entrepot_id'];
                    const id = rawId != null ? String(rawId) : '';
                    const label =
                        (w['nom'] as string) ??
                        (w['libelle'] as string) ??
                        (w['name'] as string) ??
                        (id ? `Entrepôt ${id}` : '');
                    return { id, label: label || `Entrepôt ${id}` };
                }).filter((o) => o.id !== '');
                this.seedMockTickets();
                this.recomputeList();
                this.cdr.detectChanges();
                this.refreshFeatherIcons();
            },
            error: () => {
                this.warehouseOptions = [];
                this.seedMockTickets();
                this.recomputeList();
                this.cdr.detectChanges();
            }
        });
    }

    private seedMockTickets(): void {
        const clients = [
            { name: 'Mohamed amine chaaben', phone: '27 345 216' },
            { name: 'Sarra Ben Ali', phone: '98 112 334' },
            { name: 'Entreprise Delta', phone: '71 889 001' }
        ];
        const statuses: PosTicketAvailability[] = ['disponible', 'disponible', 'consolide', 'facture_client', 'avoire', 'annule'];
        const pays: PosPaymentStatus[] = ['paye', 'non_paye', 'partiel'];
        const wh = this.warehouseOptions;
        const types = this.paymentTypeOptions;
        const rows: PosTicketRow[] = [];
        const base = new Date();
        for (let i = 0; i < 28; i++) {
            const dt = new Date(base);
            dt.setDate(base.getDate() - (i % 14));
            const c = clients[i % clients.length];
            const w =
                wh.length > 0
                    ? wh[i % wh.length]
                    : { id: null as string | null, label: '—' };
            rows.push({
                id: `tk-${i}`,
                numero: `#490ERRTEE${(9 + i).toString(36).toUpperCase()}`,
                date: dt.toISOString().split('T')[0],
                clientName: c.name,
                clientPhone: c.phone,
                entrepot: w.label,
                entrepotId: w.id != null ? String(w.id) : null,
                paymentStatus: pays[i % pays.length],
                paymentType: types[i % types.length],
                ticketStatus: statuses[i % statuses.length]
            });
        }
        this.allTickets = rows;
    }
}
